/**
 * FLIGHT-FEEL A4: power-distribution pips (SYS / ENG / WEP), Elite-style.
 *
 * A fixed budget of pips is split across three subsystems. ENG scales top speed
 * + boost effectiveness, WEP scales weapon-energy regen, SYS scales shield regen.
 * Adding a pip to one subsystem auto-rebalances by taking from the others, so the
 * total never exceeds the budget. Pure + deterministic (no Three.js, no clocks):
 * the controller owns the live PowerPips and feeds the multipliers into flight +
 * resource ticks.
 */

/** Per-subsystem pip allocation. Each 0..PIP_MAX; sum <= PIP_BUDGET. */
export interface PowerPips {
  sys: number;
  eng: number;
  wep: number;
}

/** Which subsystem a pip action targets. */
export type PipKey = keyof PowerPips;

/** Total pips available to distribute (Elite uses ~6–8; we fix 8 = even split). */
export const PIP_BUDGET = 8;
/** Max pips any single subsystem can hold. */
export const PIP_MAX = 6;

/** Even baseline: rounded thirds that sum exactly to PIP_BUDGET. */
export const balancedPips = (): PowerPips => ({ sys: 3, eng: 2, wep: 3 });

const clampPip = (v: number): number => Math.max(0, Math.min(PIP_MAX, Math.round(v)));

/** Current total across all three subsystems. */
const total = (p: PowerPips): number => p.sys + p.eng + p.wep;

/**
 * Drain `need` pips from the two NON-target subsystems, largest-first, so the
 * budget is preserved when a pip is added to `target`. Returns the reduced pair.
 */
const drainOthers = (p: PowerPips, target: PipKey, need: number): PowerPips => {
  const next: PowerPips = { ...p };
  const others = (['sys', 'eng', 'wep'] as PipKey[]).filter((k) => k !== target);
  let remaining = need;
  while (remaining > 0) {
    const donor = others.reduce((a, b) => (next[a] >= next[b] ? a : b));
    if (next[donor] <= 0) break; // nothing left to take — give up gracefully
    next[donor] -= 1;
    remaining -= 1;
  }
  return next;
};

/**
 * Add one pip to `target`, auto-rebalancing within PIP_BUDGET. If the target is
 * already at PIP_MAX or the budget is full and others are empty, the input is
 * clamped (no-op). Pure: returns a new PowerPips.
 */
export const addPip = (p: PowerPips, target: PipKey): PowerPips => {
  if (p[target] >= PIP_MAX) return { ...p };
  const overflow = total(p) + 1 - PIP_BUDGET;
  const base = overflow > 0 ? drainOthers(p, target, overflow) : { ...p };
  return { ...base, [target]: clampPip(base[target] + 1) };
};

/**
 * Set `target` to an absolute pip count (clamped 0..PIP_MAX) and rebalance the
 * others to keep the budget. Pure.
 */
export const setPip = (p: PowerPips, target: PipKey, value: number): PowerPips => {
  const v = clampPip(value);
  const overflow = v + (total(p) - p[target]) - PIP_BUDGET;
  const base = overflow > 0 ? drainOthers({ ...p, [target]: 0 }, target, overflow) : { ...p };
  return { ...base, [target]: v };
};

// --- Multipliers: each maps a 0..PIP_MAX pip count to a handling/regen factor. ---
// 0 pips -> 0.8x, scaling +0.1 per pip. At an even 2–3 pips you sit near 1.0x.

/** ENG multiplier (top speed + boost effectiveness). Range 0.8 .. 1.4. */
export const engMultiplier = (p: PowerPips): number => 0.8 + 0.1 * p.eng;
/** WEP multiplier (weapon-energy regen). Range 0.8 .. 1.4. */
export const wepMultiplier = (p: PowerPips): number => 0.8 + 0.1 * p.wep;
/** SYS multiplier (shield regen). Range 0.8 .. 1.4. */
export const sysMultiplier = (p: PowerPips): number => 0.8 + 0.1 * p.sys;
